import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Factory, X, Loader2, Power, Wrench, QrCode, RefreshCw, Copy, Trash2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface Maquina {
  id: number
  codigo: string
  nome: string
  tipo: string
  localizacao: string | null
  capacidade_hora: string
  potencia_kw: string
  custo_hora: string
  status: string
  observacoes: string | null
}

const TIPOS = ['BANBURY', 'KNEADER', 'PRENSA', 'MISTURADOR', 'CALANDRA', 'EXTRUSORA', 'CORTE', 'OUTROS']
const STATUS_COR: Record<string, string> = {
  ATIVA: 'bg-emerald-100 text-emerald-800',
  MANUTENCAO: 'bg-amber-100 text-amber-800',
  INATIVA: 'bg-ink-200 text-ink-600',
}

export default function IndMaquinasPage() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<Maquina | null | 'new'>(null)
  const [tokenModal, setTokenModal] = useState<Maquina | null>(null)

  const { data = [], isLoading } = useQuery<Maquina[]>({
    queryKey: ['ind-maquinas'],
    queryFn: async () => (await api.get('/industrial/maquinas')).data,
  })

  const mudarStatus = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) =>
      (await api.patch(`/industrial/maquinas/${id}/status`, null, { params: { status } })).data,
    onSuccess: () => {
      toast.success('Status atualizado')
      qc.invalidateQueries({ queryKey: ['ind-maquinas'] })
    },
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="text-brand-600" /> Máquinas
          </h1>
          <p className="text-sm text-ink-500">Cadastro de máquinas do chão de fábrica</p>
        </div>
        <button className="btn-primary" onClick={() => setModal('new')}>
          <Plus size={14} /> Nova máquina
        </button>
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Nome</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Tipo</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Localização</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Cap./h</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">R$/h</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Status</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={8} className="text-center py-6 text-ink-400">Carregando…</td></tr>}
            {!isLoading && data.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-ink-500">
                Nenhuma máquina cadastrada. Clique em "Nova máquina" pra começar.
              </td></tr>
            )}
            {data.map((m) => (
              <tr key={m.id}
                onClick={() => setModal(m)}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="px-3 py-2.5 font-mono text-xs">{m.codigo}</td>
                <td className="px-3 py-2.5 font-medium">{m.nome}</td>
                <td className="px-3 py-2.5 text-xs">{m.tipo}</td>
                <td className="px-3 py-2.5 text-xs text-ink-500">{m.localizacao || '—'}</td>
                <td className="px-3 py-2.5 text-right text-xs">{Number(m.capacidade_hora) || '—'}</td>
                <td className="px-3 py-2.5 text-right text-xs">{Number(m.custo_hora).toFixed(2)}</td>
                <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                  <select
                    value={m.status}
                    onChange={(e) => mudarStatus.mutate({ id: m.id, status: e.target.value })}
                    className={`text-[11px] font-medium px-2 py-0.5 rounded border-0 ${STATUS_COR[m.status]}`}
                  >
                    <option value="ATIVA">ATIVA</option>
                    <option value="MANUTENCAO">MANUTENÇÃO</option>
                    <option value="INATIVA">INATIVA</option>
                  </select>
                </td>
                <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setTokenModal(m)}
                      title="Token de pareamento (kiosk)"
                      className="text-ink-500 hover:text-violet-600 p-1"
                    >
                      <QrCode size={14} />
                    </button>
                    <button onClick={() => setModal(m)} className="text-ink-500 hover:text-brand-600 p-1">
                      <Edit size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <MaquinaModal
          maquina={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { qc.invalidateQueries({ queryKey: ['ind-maquinas'] }); setModal(null) }}
        />
      )}
      {tokenModal && (
        <TokenPareamentoModal maquina={tokenModal} onClose={() => setTokenModal(null)} />
      )}
    </div>
  )
}


function TokenPareamentoModal({ maquina, onClose }: { maquina: Maquina; onClose: () => void }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  async function load() {
    setLoading(true)
    try {
      const r = await api.get(`/industrial/maquinas/${maquina.id}/token`)
      setData(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally { setLoading(false) }
  }

  async function regenerar() {
    if (!confirm('Gerar NOVO token? O anterior será invalidado e o tablet pareado será desconectado.')) return
    try {
      const r = await api.post(`/industrial/maquinas/${maquina.id}/gerar-token`)
      setData(r.data)
      toast.success('Novo token gerado')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  async function revogar() {
    try {
      await api.delete(`/industrial/maquinas/${maquina.id}/token`)
      onClose()
      toast.success('Pareamento revogado')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  useEffect(() => { load() }, [])

  const kioskUrl = data?.token ? `${window.location.origin}/kiosk/${data.token}` : ''

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <QrCode className="h-5 w-5 text-violet-600" /> Token de pareamento
            </h2>
            <div className="text-sm text-ink-500">{maquina.codigo} — {maquina.nome}</div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        {loading && <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-violet-600 mx-auto" /></div>}

        {data && (
          <>
            {data.pareada_em ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
                ✓ Pareada desde {new Date(data.pareada_em).toLocaleString('pt-BR')}
                {data.device_info && <div className="text-xs text-emerald-700 mt-1 font-mono">{data.device_info}</div>}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                Aguardando primeiro pareamento. Bipa o QR ou cola o token no tablet em /kiosk
              </div>
            )}

            <div className="bg-white border border-ink-200 rounded-lg p-4 flex flex-col items-center">
              <QRCodeSVG value={kioskUrl} size={220} level="M" />
              <div className="text-xs text-ink-500 mt-2 font-mono break-all text-center">{kioskUrl}</div>
            </div>

            <div>
              <label className="text-xs uppercase text-ink-500 font-semibold">Token</label>
              <div className="flex gap-2 mt-1">
                <input
                  readOnly
                  value={data.token}
                  className="flex-1 px-3 py-2 border border-ink-200 rounded font-mono text-xs bg-ink-50"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(data.token); toast.success('Token copiado') }}
                  className="px-3 py-2 border border-ink-300 rounded hover:bg-ink-50"
                  title="Copiar"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex justify-between pt-2 border-t border-ink-100">
              <button
                onClick={() => pedirExclusao({ descricao: 'o token da máquina', onConfirm: revogar })}
                className="text-sm text-red-600 hover:bg-red-50 px-3 py-2 rounded flex items-center gap-1"
              >
                <Trash2 className="h-4 w-4" /> Revogar pareamento
              </button>
              <button
                onClick={regenerar}
                className="text-sm bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 rounded flex items-center gap-1"
              >
                <RefreshCw className="h-4 w-4" /> Gerar novo token
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}


function MaquinaModal({
  maquina, onClose, onSuccess,
}: { maquina: Maquina | null; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    codigo: maquina?.codigo || '',
    nome: maquina?.nome || '',
    tipo: maquina?.tipo || 'PRENSA',
    localizacao: maquina?.localizacao || '',
    capacidade_hora: Number(maquina?.capacidade_hora) || 0,
    potencia_kw: Number(maquina?.potencia_kw) || 0,
    custo_hora: Number(maquina?.custo_hora) || 0,
    observacoes: maquina?.observacoes || '',
  })

  const mut = useMutation({
    mutationFn: async () => {
      if (maquina) {
        return (await api.put(`/industrial/maquinas/${maquina.id}`, form)).data
      }
      return (await api.post('/industrial/maquinas', form)).data
    },
    onSuccess: () => {
      toast.success(maquina ? 'Máquina atualizada' : 'Máquina cadastrada')
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const valido = form.codigo.trim() && form.nome.trim() && form.tipo

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Factory size={18} /> {maquina ? `Editar ${maquina.nome}` : 'Nova máquina'}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Código *</label>
              <input className="input" value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: PRENSA-01" />
            </div>
            <div>
              <label className="label">Tipo *</label>
              <select className="input" value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Nome *</label>
            <input className="input" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex: Prensa Hidráulica 100T" />
          </div>
          <div>
            <label className="label">Localização</label>
            <input className="input" value={form.localizacao}
              onChange={(e) => setForm({ ...form, localizacao: e.target.value })}
              placeholder="Setor A / Box 3" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Capacidade/hora</label>
              <input type="number" step="0.001" className="input" value={form.capacidade_hora}
                onChange={(e) => setForm({ ...form, capacidade_hora: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Potência (kW)</label>
              <input type="number" step="0.01" className="input" value={form.potencia_kw}
                onChange={(e) => setForm({ ...form, potencia_kw: Number(e.target.value) })} />
            </div>
            <div>
              <label className="label">Custo/hora (R$)</label>
              <input type="number" step="0.01" className="input" value={form.custo_hora}
                onChange={(e) => setForm({ ...form, custo_hora: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <label className="label">Observações</label>
            <textarea className="input" rows={2} value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button className="btn-primary flex-1" disabled={!valido || mut.isPending}
              onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
